import React, { useEffect, useState } from "react";
import {
    Button,
    UploadingInput,
    Select,
} from "../../../../../common";
import { useSelector } from "react-redux";
import { ReactComponent as Close } from "../../../../../../assets/icons/close-circle.svg";
import {
    useGetAvailableCategoryQuery,
    useCreateCorporateTaxMutation,
    useUpdateCategoryMutation,
} from "../../../../../../store/Company/companyApiSlice";
import { ReactComponent as Spinner } from "../../../../../../assets/icons/spinner.svg";
import { uploadFilePath } from "../../../../../../constants/s3UploadPath";
import { uploadFileToS3 } from "../../../../../../utils/uploadFileToS3";
import CreatableSelectComponent from "../../../../../common/CreatableSelectComponent";

export const AddTaxInformation = ({
    editCategory,
    modalHandle,
    refetch, compRefetch, dataChanged, setDataChanged
}) => {
    const { currentEditingCompany } = useSelector((state) => state.company);
    const { userInfo, token } = useSelector((state) => state.auth);
    const [errors, setErrors] = useState({
        taxNumber: "",
        category: "",
        tradePhotoError: ""
    });
    const [uploadedFile, setUploadedFile] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [isFileUploaded, setIsFileUploaded] = useState(false);
    const [progress, setProgress] = useState(50);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [taxNumber, setTaxNumber] = useState("");
    const { data: availableCategory } = useGetAvailableCategoryQuery({ token: token, id: currentEditingCompany?.companyID });
    const [categories, setCategories] = useState([]);
    const [createCorporateTax, { isLoading: isCreatingCorporateTax }] = useCreateCorporateTaxMutation();

    const handleFormatOptions = (options) => {
        return options?.map(value => ({
            value: value.categoryTitle,
            label: value.categoryTitle + " (" + value.categoryAbbr + ")",
        }))
    }

    useEffect(() => {
        const categoryTitles = availableCategory?.length > 0 ? handleFormatOptions(availableCategory) : [];
        setCategories(categoryTitles)
    }, [availableCategory])
    const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    const ALLOWED_PDF_TYPE = "application/pdf";
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

    const renderPreview = () => {
        if (!uploadedFile) return null;

        // ✅ File size check
        if (uploadedFile.size > MAX_FILE_SIZE) {
            return <p>File too large</p>;
        }

        const isImage = ALLOWED_IMAGE_TYPES.includes(uploadedFile.type);
        const isPDF = uploadedFile.type === ALLOWED_PDF_TYPE;

        // ❌ Reject unsafe types
        if (!isImage && !isPDF) {
            return <p>Unsupported or unsafe file type</p>;
        }

        // ✅ Safe object URL creation (only when needed)
        let objectUrl = null;
        if (isPDF) {
            objectUrl = URL.createObjectURL(uploadedFile);
        }

        if (isImage) {
            return (
                <img
                    src={previewImage}
                    alt="trade"
                    className="w-18"
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = ""; // prevent broken/malicious fallback
                    }}
                />
            );
        } else if (isPDF) {
            return (
                <iframe
                    src={objectUrl}
                    title="PDF preview"
                    width="100%"
                    height="500px"
                    sandbox="" // ✅ restricts script execution
                    onLoad={() => {
                        // ✅ cleanup after load
                        if (objectUrl) {
                            URL.revokeObjectURL(objectUrl);
                        }
                    }}
                />
            );
        } else {
            return <p>Unsupported file type</p>;
        }
    };
    const [updateCategoryMutation, { isLoading: isUpdating }] =
        useUpdateCategoryMutation();

    const fetchFileFromURL = async (url) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            // Get filename from URL or use a default name
            const fileName = url.split('/').pop() || 'downloaded-file';
            // Create a File object from the blob
            const file = new File([blob], fileName, { type: blob.type });
            return file;
        } catch (error) {
            console.error('Error fetching file:', error);
            return null;
        }
    };

    useEffect(() => {
        if (editCategory) {
            setTaxNumber(editCategory?.tax_reg_no);
            setSelectedCategory({ value: editCategory?.tax_category_title, label: editCategory?.tax_category_title + " (" + editCategory?.tax_abbr + ")" });

            // Fetch and set the file if upload_file URL exists
            if (editCategory?.upload_file) {
                fetchFileFromURL(editCategory.upload_file).then(file => {
                    if (file) {
                        setUploadedFile(file);
                        setProgress(100);

                        // Handle preview for images
                        if (file.type.match("image.*")) {
                            const reader = new FileReader();
                            reader.onload = (e) => {
                                setPreviewImage(e.target.result);
                            };
                            reader.readAsDataURL(file);
                        }
                    }
                });
            }
        }
    }, [editCategory]);

    const handleFileSelect = (file) => {
        setIsFileUploaded(true);
        setUploadedFile(file);
        setProgress(100);
        setErrors(prev => ({ ...prev, tradePhotoError: "" }));

        if (file && file.type.match("image.*")) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setPreviewImage(e.target.result);
            };
            reader.readAsDataURL(file);
        } else {
            setPreviewImage(null);
        }
    };

    function convertFileSize(size) {
        if (size < 1024) {
            return size + " B";
        } else if (size < 1024 * 1024) {
            return (size / 1024).toFixed(2) + " KB";
        } else {
            return (size / (1024 * 1024)).toFixed(2) + " MB";
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();

        let newErrors = {};

        if (!taxNumber?.trim()) {
            newErrors.taxNumber = "Tax number is required";
        }

        if (!selectedCategory?.value) {
            newErrors.category = "Category is required";
        }

        if (!uploadedFile && !editCategory?.upload_file) {
            newErrors.tradePhotoError = "Please upload supporting document";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        let taxCertificateUrl = null;

        if (uploadedFile && isFileUploaded) {
            taxCertificateUrl = await uploadFileToS3(
                uploadFilePath?.tax,
                uploadedFile,
                token,
                currentEditingCompany?.companyID
            );
        }

        if (editCategory) {
            const response = await updateCategoryMutation({
                data: {
                    tax_id: editCategory?.tax_id,
                    company_id: currentEditingCompany?.companyID,
                    tax_category_title: selectedCategory?.value,
                    tax_reg_no: taxNumber,
                    upload_file: isFileUploaded
                        ? taxCertificateUrl?.url
                        : editCategory?.upload_file
                },
                token,
            });

            if (response) {
                modalHandle();
                refetch();
                compRefetch();
                setDataChanged(!dataChanged);
            }
        } else {
            const response = await createCorporateTax({
                data: {
                    company_id: currentEditingCompany?.companyID,
                    tax_category_title: selectedCategory?.value,
                    tax_reg_no: taxNumber,
                    upload_file: taxCertificateUrl?.url
                },
                token,
            });

            if (response) {
                modalHandle();
                refetch();
                compRefetch();
                setDataChanged(!dataChanged);
            }
        }
    };


    return (
        <div className="">
            <div className="text-left font-bold text-f16">
                Add Tax Information
            </div>
            <label
                htmlFor="tax_reg_no"
                className="block mb-1 text-primary text-f14 font-semibold text-left "
            >
                Tax Number
            </label>

            <input
                value={taxNumber}
                placeholder="Enter"
                onChange={(e) => {
                    setTaxNumber(e.target.value);
                    setErrors(prev => ({ ...prev, taxNumber: "" }));
                }}
                className="focus:border-primary px-3 py-2 border-input rounded bg-input w-full border mb-2"
            />
            {errors.taxNumber && (
                <p className="text-red-500 text-sm text-left">
                    {errors.taxNumber}
                </p>
            )}
            {<div>
                <CreatableSelectComponent
                    label={"Category"}
                    id="countryCode"
                    placeholder={"Select Category"}
                    value={selectedCategory}
                    options={categories}
                    setOptions={setCategories}
                    setSelectedValue={(value) => {
                        setSelectedCategory(value);
                        setErrors(prev => ({ ...prev, category: "" }));
                    }}
                    isCreatable={true}
                    classes={"mb-3"}
                />
                {errors.category && (
                    <p className="text-red-500 text-sm text-left">
                        {errors.category}
                    </p>
                )}

                {/* <Select
                    label={`Category`}
                    id="countryCode"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    classes="text-left"
                >
                    {availableCategory?.map((category) => (
                        <option key={category.tax_categoryID} value={category.categoryTitle}>
                            {category.categoryTitle + " (" + category.categoryAbbr + ")"}
                        </option>
                    ))}
                </Select> */}
            </div>}
            <label className="block mb-1 mt-5 text-primary text-f14 font-semibold text-left">
                Upload Supporting Document
            </label>

            <UploadingInput onFileSelect={handleFileSelect} />
            {uploadedFile && (
                <div className="bg-slate mt-2 p-3 rounded-md relative ">
                    <div className="flex gap-3">
                        <div
                            className={`flex-auto w-16 ${uploadedFile.type === "application/pdf" ? "h-20" : "h-14"
                                } overflow-hidden`}
                        >
                            {renderPreview()}
                        </div>
                        <div className="flex flex-col items-start justify-center text-secondarytwo">
                            <p className="text-f14">{uploadedFile.name}</p>
                            <div>
                                <span className="text-f12 text-secondary">
                                    {convertFileSize(uploadedFile.size)}
                                </span>
                            </div>
                        </div>
                    </div>
                    {true && (
                        <div className="flex gap-3 items-center justify-start mt-3">
                            <div className="bg-white h-2 rounded-full overflow-hidden  relative w-full">
                                <div
                                    className="bg-primary h-full transition-all"
                                    style={{ width: `${progress}%` }}
                                ></div>
                            </div>
                            <span className="">{progress}%</span>
                        </div>
                    )}

                    {uploadedFile && (
                        <button
                            className="absolute right-3 top-3"
                            onClick={() => setUploadedFile(null)}
                        >
                            <Close />
                        </button>
                    )}
                </div>
            )}
            {errors.tradePhotoError && (
                <p className="text-red-500 text-sm text-left">
                    {errors.tradePhotoError}
                </p>
            )}

            <Button
                classes="w-full p-2 bg-primary text-white rounded text-f18 mt-4 flex justify-center"
                onClick={handleSubmit}
            >
                {isUpdating || isCreatingCorporateTax ? (
                    <Spinner />
                ) : (
                    "Submit"
                )}
            </Button>
        </div>
    );
};
